import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from "rxjs";
import {DjangoApiService} from "../../../../shared/services/django-api.service";
import {SnackbarService} from "../../../../shared/services/snackbar.service";
import {LanguageService} from "../../../../shared/services/language.service";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {ActivatedRoute, Router} from "@angular/router";
import {CardsBasicComponent} from "../../../../shared/components/cards/cards-basic/cards-basic.component";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {SlugTransformDirective} from "../../../../shared/directives/slug-transform.directive";

@Component({
  selector: 'app-product-settings',
  standalone: true,
  imports: [
    CardsBasicComponent,
    FormsModule,
    MatButton,
    MatCheckbox,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    TranslateModule,
    SlugTransformDirective,
  ],
  templateUrl: './product-settings.component.html',
  styleUrl: './product-settings.component.css'
})
export class ProductSettingsComponent implements OnInit, OnDestroy {
  productId!: number;
  productData!: any;
  isLoading: boolean = false;
  isLoadingPrices: boolean = false;
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private _apiService: DjangoApiService,
    private _snackbarService: SnackbarService,
    public _languageService: LanguageService,
    private _translate: TranslateService,
    private _route: ActivatedRoute,
    private _router: Router) {
  }

  ngOnInit() {
    this.setupLanguageSubscription();
    this.getRouteParams();
  }

  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }

  private getRouteParams() {
    this.subscriptions.add(
      this._route.params.subscribe(params => {
        this.productId = this._route.snapshot.parent?.params['productId'];
      })
    );

    if (!isNaN(Number(this.productId))) {
      this.loadProductData();
    } else {
      this.createEmptyProduct();
    }
  }

  private createEmptyProduct() {
    const translations: { [key: string]: { title: string, slug: string, description: string } } = {};

    this._languageService.supportedLangs.forEach(lang => {
      translations[lang] = {
        title: '',
        slug: '',
        description: '',
      };
    });

    this.productData = {
      file_url: '',
      excel_sheet_name: '',
      file_product_id: '',
      file_category: '',
      file_type: '',
      translations: translations
    };
  }

  private loadProductData() {
    this.subscriptions.add(
      this._apiService.fetchProductDetail(this.productId).subscribe({
        next: (response: any) => {
          this.productData = response;
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  importPricesFromUrl(){
    this.isLoadingPrices = true;
    this.subscriptions.add(
      this._apiService.importPricesFromUrl(this.productId).subscribe({
        next: (response: any) => {
          this.isLoadingPrices = false;
          this._snackbarService.show('Prices successfully imported.');
        },
        error: (error: any) => {
          this.isLoadingPrices = false;
          this._snackbarService.show('Error importing prices.');
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  private createProduct() {
    this.isLoading = true;
    this.subscriptions.add(
      this._apiService.createProduct(this.productData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          this.productData = response.data;
          this._snackbarService.show(response.message);
          this.updateUrl();
        },
        error: (error: any) => {
          this.isLoading = false;
          this._snackbarService.show('There was an error');
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  private updateProduct() {
    this.isLoading = true;
    this.subscriptions.add(
      this._apiService.updateProductDetail(this.productData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          this.productData = response.data;
          this._snackbarService.show(response.message);
        },
        error: (error: any) => {
          this.isLoading = false;
          this._snackbarService.show('There was an error');
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  saveProduct() {
    this.productData.id ? this.updateProduct() : this.createProduct();
  }

  cancel() {
    this.productData.id ? this.loadProductData() : this.createEmptyProduct();
  }

  private updateUrl(): void {
    this._router.navigateByUrl(this._router.url.replace(String(this.productId), String(this.productData.id)));
    this.productId = this.productData.id;
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

}
